import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import styles from "./ProductDetailPage.module.css";
import WebService from "../../Services/WebService";
import { mediaUrl } from "../../Services/mediaUrl";
import { toast } from "react-toastify";
import { FaTag, FaEllipsisV, FaPlus } from "react-icons/fa";
import AdjustStockModal from "../AdjustStockModal";
import StockHistory from "../StockHistory";
import { InventoryItemType, StockHistoryView } from "../inventoryHistory";
import { useBladeBack, useInBlade } from "../../Customs/FullWidthBlade/FullWidthBlade";

interface ProductImage {
  document_id: number;
  file_name: string;
}

interface ProductDetail {
  id: number;
  sku_id: string;
  name: string;
  category: string;
  category_id: number;
  quantity: number;
  measuring_unit: string;
  measuring_unit_id: number;
  measuring_unit_value: number;
  prefix: string;
  prefix_id: number;
  basic_door_price: number;
  double_door_price: number;
  back_design_price: number;
  back_puc_price: number;
  sagwan_door_price: number;
  qualities: { quality_id?: number; id?: number; name?: string; quality_name?: string; price?: number | null }[];
  gst_percentage: number;
  is_on_sale: boolean;
  distributor_sale_discount_per: number;
  customer_sale_discount_per: number;
  is_return_allowed: boolean;
  design_description: string;
  is_new_arrival: boolean;
  is_most_selling?: boolean;
  status: string;
  visibility: string;
  customer_ids: number[];
  images: ProductImage[];
}

const defaultProduct: ProductDetail = {
  id: 0,
  sku_id: "",
  name: "",
  category: "",
  category_id: 0,
  quantity: 0,
  measuring_unit: "",
  measuring_unit_id: 0,
  measuring_unit_value: 0,
  prefix: "",
  prefix_id: 0,
  basic_door_price: 0,
  double_door_price: 0,
  back_design_price: 0,
  back_puc_price: 0,
  sagwan_door_price: 0,
  qualities: [],
  gst_percentage: 0,
  is_on_sale: false,
  distributor_sale_discount_per: 0,
  customer_sale_discount_per: 0,
  is_return_allowed: false,
  design_description: "",
  is_new_arrival: false,
  status: "",
  visibility: "PUBLIC",
  customer_ids: [],
  images: [],
};

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const handleBack = useBladeBack("/inventory/product-design");
  const inBlade = useInBlade();
  const stateProduct = (location.state as any)?.product as ProductDetail | undefined;

  const [, setLoading] = useState(false);
  const [product, setProduct] = useState<ProductDetail>(stateProduct || defaultProduct);
  const [, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAdjustStock, setShowAdjustStock] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "transactions" | "inventory">("details");

  // --- Image upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);

  const normalizeProduct = (raw: any): ProductDetail => {
    const qualities = Array.isArray(raw?.qualities)
      ? raw.qualities.map((q: any) => ({
          quality_id: q.quality_id ?? q.id,
          id: q.id ?? q.quality_id,
          name: q.name ?? q.quality_name ?? q.label,
          quality_name: q.quality_name ?? q.name ?? q.label,
          price: q.price,
        }))
      : [];
    return { ...defaultProduct, ...raw, qualities };
  };

  // --- Fetch product details
  const fetchProduct = async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/product/${id}`,
      });
      if (!res?.data) {
        setError("Product not found.");
        setProduct(defaultProduct);
      } else {
        setProduct(normalizeProduct(res.data));
      }
    } catch {
      setError("Failed to fetch product.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stateProduct && stateProduct.id === Number(id)) {
      setProduct(normalizeProduct(stateProduct));
    }
    fetchProduct();
    // eslint-disable-next-line
  }, [id]);

  // --- Menu outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      const menu = document.getElementById("product-dot-menu");
      if (menu && !menu.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  // --- Status toggle
  const handleToggleStatus = async () => {
    if (!product) return;
    setUpdatingStatus(true);
    const newStatus = product.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      const res = await WebService.putAPI({
        action: `/v1/door-management/product`,
        body: { ...product, status: newStatus },
      }) as { status_code?: number; message?: string };
      if (res?.status_code === 200) {
        setProduct((prev) => ({ ...prev, status: newStatus }));
        toast.success("Status updated successfully!");
      } else {
        toast.error(res?.message || "Failed to update status");
      }
    } catch (e) {
      // toast.error("Failed to update status");
    }
    setUpdatingStatus(false);
    setMenuOpen(false);
  };

  // --- Image upload
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploading(true);

    // Preview
    const reader = new FileReader();
    reader.onloadend = () => setUploadPreview(reader.result as string);
    reader.readAsDataURL(file);

    try {
      await WebService.fileUploadAPI({
        action: `/v1/door-management/product/images/${id}`,
        key: "file",
        file: file,
        body: {},
      });
      toast.success("Image uploaded!");
      setTimeout(() => {
        setUploadPreview(null);
        fetchProduct();
      }, 450);
    } catch {
      toast.error("Failed to upload image.");
    }
    setUploading(false);
    // Reset file input for single upload
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatCost = (value: number | string | null | undefined) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? `₹${amount}` : "—";
  };

  // --- Selling cost data
  const sellingCost = [
    { label: "Single door, Back door, Bubble packaging", value: product.basic_door_price },
    { label: "Double Door", value: product.double_door_price },
    { label: "Back Design", value: product.back_design_price },
    { label: "Back PVC", value: product.back_puc_price },
    { label: "Sagwan Bidding", value: product.sagwan_door_price },
    ...(product.qualities || []).map((q) => ({
      label: q.name || q.quality_name || "Wood Quality",
      value: q.price,
      alwaysShow: true,
    })),
  ];

  // --- Visibility Status Display
  const visibilityLabel =
    product.visibility === "PUBLIC"
      ? "Visible to all"
      : product.visibility === "EXCLUDE"
      ? "Visible to all except"
      : "Visible only to selected";

  // --- Stock status
  let stockStatus = "";
  if (product.quantity === 0) stockStatus = "Out of Stock";
  else if (product.quantity <= 5) stockStatus = "Low Stock";
  else stockStatus = "In Stock";

  // --- Main Render ---
  return (
    <div
      className={styles.pageContainer}
      style={inBlade ? { minHeight: "100%", paddingTop: 16 } : undefined}
    >
      <button className={styles.backBtn} onClick={handleBack}>
        {"←"} Back to Product List
      </button>
      <div className={styles.headerRow}>
        <div className={styles.tabsRow}>
          <div
            className={`${styles.tab} ${activeTab === "details" ? styles.active : ""}`}
            onClick={() => { setActiveTab("details"); setMenuOpen(false); }}
          >
            Details
          </div>
          <div
            className={`${styles.tab} ${activeTab === "transactions" ? styles.active : ""}`}
            onClick={() => { setActiveTab("transactions"); setMenuOpen(false); }}
          >
            Transaction History
          </div>
          <div
            className={`${styles.tab} ${activeTab === "inventory" ? styles.active : ""}`}
            onClick={() => { setActiveTab("inventory"); setMenuOpen(false); }}
          >
            Inventory History
          </div>
        </div>
        {activeTab === "details" && (
        <div style={{ position: "relative" }}>
          <button className={styles.menuButton} onClick={() => setMenuOpen((v) => !v)} title="Actions">
            <FaEllipsisV size={20} />
          </button>
          {menuOpen && (
            <div id="product-dot-menu" className={styles.menuDropdown}>
              <div className={styles.menuLabel}>Actions</div>
              <div className={styles.menuOption} onClick={() => { setShowAdjustStock(true); setMenuOpen(false); }}>
                Adjust Stock
              </div>
              <div className={styles.menuOption}>
                <span>Mark as {product.status === "ACTIVE" ? "Inactive" : "Active"}</span>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={product.status === "ACTIVE"}
                    onChange={handleToggleStatus}
                    disabled={updatingStatus}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      {activeTab === "details" ? (
        <>
     <div className={styles.mainGrid}>
  {/* --- Product Main Card --- */}
  <div className={styles.mainCard}>
    {/* --- Images & Upload Section --- */}
    <div className={styles.imageUploadSection}>
      <div className={styles.imagesContainer}>
        {product.images && product.images.length > 0 ? (
          product.images.map((img) => (
            <img
              key={img.document_id}
              src={mediaUrl(img.file_name)}
              alt="product"
              className={styles.detailImg}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  process.env.PUBLIC_URL + "/Assets/no-pictures.png";
              }}
            />
          ))
        ) : (
          <img
            src={process.env.PUBLIC_URL + "/Assets/no-pictures.png"}
            alt="No images"
            className={styles.detailImg}
          />
        )}
      </div>
      <div className={styles.uploadBox}>
        <button
          type="button"
          className={styles.uploadBtn}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <FaPlus style={{ marginRight: 5 }} />
          {uploading ? "Uploading..." : "Add Image"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageChange}
          disabled={uploading}
        />
        {uploadPreview && (
          <div className={styles.previewBox}>
            <img src={uploadPreview} alt="preview" className={styles.previewImg} />
            <span style={{ fontSize: 13, color: "#666" }}>Preview</span>
          </div>
        )}
      </div>
    </div>
    {/* --- Product Meta --- */}
    <div className={styles.metaRow}>
      <div className={styles.metaCol}>
        <span className={styles.metaLabel}>Item Code</span>
        <span className={styles.metaValue}>{product.sku_id}</span>
      </div>
      <div className={styles.metaCol}>
        <span className={styles.metaLabel}>Item Name</span>
        <span className={styles.metaValue}>{product.name}</span>
      </div>
      <div className={styles.metaCol}>
        <span className={styles.metaLabel}>Category</span>
        <span className={styles.categoryTag}>
          <FaTag style={{ marginRight: 4, color: "#26b365" }} />
          {product.category}
        </span>
      </div>
      <div className={styles.metaCol}>
        <span className={styles.metaLabel}>Prefix</span>
        <span className={styles.metaValue}>{product.prefix}</span>
      </div>
      <div className={styles.metaCol}>
        <span className={styles.metaLabel}>Quantity</span>
        <span className={styles.metaValue}>{product.quantity}</span>
      </div>
    </div>
    <div className={styles.priceRow}>
      <div>
        <span className={styles.priceLabel}>Selling Price</span>
        <span className={styles.priceValue}>₹{product.basic_door_price}</span>
        <span className={styles.unitText}>per {product.measuring_unit}</span>
      </div>
      <div>
        <span className={styles.priceLabel}>Purchase Cost</span>
        <span className={styles.priceValue}>₹{product.double_door_price}</span>
        <span className={styles.unitText}>per {product.measuring_unit}</span>
      </div>
    </div>
  </div>

  {/* --- Selling Cost Card --- */}
  <div className={styles.sellingCostCard} style={{ height: "100%" }}>
    <div className={styles.cardTitle}>Selling Cost</div>
    <div className={styles.sellingList}>
      {sellingCost.map((row) =>
        row.value ? (
          <div key={row.label} className={styles.sellingRow}>
            <span>{row.label}</span>
            <span>{formatCost(row.value)}</span>
          </div>
        ) : null
      )}
    </div>
  </div>

  {/* --- Restrictions & Availability Card --- */}
  <div className={styles.sideCard} style={{ height: "100%" }}>
    <div className={styles.cardTitle}>Restrictions & Availability</div>
    <div>
      <div>
        <span>New arrival: </span>
        <span>{product.is_new_arrival ? "Yes" : "No"}</span>
      </div>
      <div>
        <span>Trending: </span>
        <span>{product.is_most_selling ? "Yes" : "No"}</span>
      </div>
      <div>
        <span>Is return allowed: </span>
        <span>{product.is_return_allowed ? "Yes" : "No"}</span>
      </div>
      <div>
        <span>Distributor Discount: </span>
        <span>{product.is_on_sale ? `${product.distributor_sale_discount_per}%` : "-"}</span>
      </div>
      <div>
        <span>Retailer Discount: </span>
        <span>{product.is_on_sale ? `${product.customer_sale_discount_per}%` : "-"}</span>
      </div>
      <div>
        <span>GST %: </span>
        <span>{product.gst_percentage}%</span>
      </div>
    </div>
  </div>
</div>

{/* --- Visibility Status Card, BELOW mainGrid --- */}
<div className={styles.statusCard}>
  <div className={styles.cardTitle}>Visibility Status</div>
  <div>
    <span>{visibilityLabel}</span>
  </div>
  <div>
    <span className={styles.stockStatus}>{stockStatus}</span>
    <span className={styles.stockUnits}>{product.quantity} units available</span>
  </div>
</div>

      {/* --- Description at bottom --- */}
      <div className={styles.descriptionBox}>
        <span className={styles.cardTitle}>Description</span>
        <p className={styles.description}>{product.design_description || "-"}</p>
      </div>
      {/* --- Adjust Stock Modal --- */}
      {showAdjustStock && (
        <AdjustStockModal
          open={showAdjustStock}
          onClose={() => setShowAdjustStock(false)}
          product={{
            id: product.id,
            name: product.name,
            sku_id: product.sku_id,
            images: product.images,
            quantity: product.quantity,
          }}
          mode="product"
          onSuccess={() => {
            setShowAdjustStock(false);
            fetchProduct();
          }}
        />
      )}
        </>
      ) : (
        <StockHistory
          itemType={InventoryItemType.PRODUCT}
          itemId={product.id}
          view={activeTab === "inventory" ? StockHistoryView.INVENTORY : StockHistoryView.TRANSACTIONS}
        />
      )}
    </div>
  );
};

export default ProductDetailPage;
